import { useMemo, useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search, FileText, PlusCircle, Settings } from "lucide-react";
import { getEmailSnippets, type EmailSnippetView } from "../../api/EmailSnippetApi";
import { getProjects } from "../../api/ProjectApi";
import { Tooltip } from "../../components/ui";

interface Props {
  onSelect: (snippet: EmailSnippetView) => void;
  onCreateSnippet?: () => void;
  onManageSnippets?: () => void;
  iconOnly?: boolean;
}

const EmailSnippetPicker = ({
  onSelect,
  onCreateSnippet,
  onManageSnippets,
  iconOnly = false,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  const { data: snippets = [], isLoading } = useQuery<EmailSnippetView[]>({
    queryKey: ["email-snippets"],
    queryFn: getEmailSnippets,
    staleTime: 60_000 * 5,
    enabled: open,
  });

  const { data: projects = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["projects-map"],
    queryFn: () =>
      getProjects().then((list: any[]) => list.map((p) => ({ id: p.id, name: p.name }))),
    staleTime: 60_000 * 10,
    enabled: open,
  });

  const projectMap = useMemo(() => {
    const map: Record<string, string> = {};
    projects.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [projects]);

  const filtered = useMemo(() => {
    if (!search) return snippets;
    const lower = search.toLowerCase();
    return snippets.filter((s) => (s.name || "").toLowerCase().includes(lower));
  }, [snippets, search]);

  const personal = useMemo(() => filtered.filter((s) => s.visibility === "Private"), [filtered]);

  const pipelineGroups = useMemo(() => {
    const shared = filtered.filter((s) => s.visibility === "Shared");
    const groups: Record<string, EmailSnippetView[]> = {};
    shared.forEach((s) => {
      const key = s.projectId || "__shared__";
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    });
    return groups;
  }, [filtered]);

  const isEmpty = personal.length === 0 && Object.keys(pipelineGroups).length === 0;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
    setOpen((v) => !v);
  };

  const handleSelect = (snippet: EmailSnippetView) => {
    onSelect(snippet);
    setOpen(false);
    setSearch("");
  };

  return (
    <span className="relative inline-flex">
      {/* Trigger button — matches Add Variable style */}
      <Tooltip text={iconOnly ? t("Templates") : ""}>
        <button
          ref={triggerRef}
          type="button"
          onClick={handleOpen}
          title={t("Templates")}
          className="inline-flex items-center justify-center w-[28px] h-[24px] text-gray-700 hover:text-blue-600 transition-colors focus:outline-none"
        >
          <FileText size={15} strokeWidth={2} className="shrink-0" />
          {!iconOnly && <span className="ml-1 text-xs">{t("Templates")}</span>}
        </button>
      </Tooltip>

      {/* Dropdown — fixed to escape modal overflow */}
      {open && (
        <div
          ref={ref}
          style={{ top: dropdownPos.top, right: dropdownPos.right }}
          className="fixed z-[200] w-72 bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col overflow-hidden"
        >
          {/* Search */}
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="flex items-center gap-2 bg-gray-100 rounded-md px-2.5 py-1.5">
              <Search size={13} className="text-gray-400 shrink-0" />
              <input
                autoFocus
                className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400 text-gray-800"
                placeholder={t("Search templates...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-64 overflow-y-auto py-1">
            {isLoading ? (
              <div className="py-6 text-center text-sm text-gray-400">{t("Loading...")}</div>
            ) : isEmpty ? (
              <div className="py-6 text-center text-sm text-gray-400">
                {t("No templates found.")}
              </div>
            ) : (
              <>
                {personal.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      {t("Personal")}
                    </div>
                    {personal.map((s) => (
                      <SnippetRow key={s.id} snippet={s} onSelect={handleSelect} />
                    ))}
                  </>
                )}
                {Object.entries(pipelineGroups).map(([projectId, items]) => (
                  <div key={projectId}>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      {projectMap[projectId] || t("Shared")}
                    </div>
                    {items.map((s) => (
                      <SnippetRow key={s.id} snippet={s} onSelect={handleSelect} />
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          {(onCreateSnippet || onManageSnippets) && (
            <div className="border-t border-gray-100 p-2 flex flex-col gap-1.5">
              {onCreateSnippet && (
                <div
                  role="button"
                  onClick={() => {
                    onCreateSnippet();
                    setOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors cursor-pointer"
                >
                  <PlusCircle size={14} className="shrink-0" />
                  {t("Create snippet from draft")}
                </div>
              )}
              {onManageSnippets && (
                <div
                  role="button"
                  onClick={() => {
                    onManageSnippets();
                    setOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors cursor-pointer"
                >
                  <Settings size={14} className="shrink-0" strokeWidth={1.5} />
                  {t("Manage Snippets")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </span>
  );
};

const SnippetRow = ({
  snippet,
  onSelect,
}: {
  snippet: EmailSnippetView;
  onSelect: (s: EmailSnippetView) => void;
}) => (
  <div
    role="button"
    onClick={() => onSelect(snippet)}
    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors group cursor-pointer"
  >
    <FileText size={13} className="text-gray-400 shrink-0 group-hover:text-blue-500" />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-gray-800 truncate">{snippet.name || "Untitled"}</div>
    </div>
    {snippet.tags && snippet.tags.length > 0 && (
      <span className="text-[11px] text-gray-400 shrink-0">{snippet.tags[0]}</span>
    )}
  </div>
);

export default EmailSnippetPicker;
